import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Star, MessageSquare, TrendingUp } from 'lucide-react';
import kitchenApi from '../services/kitchenApiService';

export default function RatingsPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await kitchenApi.getRatings({ page });
    if (res.success) setData(res.data);
    setLoading(false);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const avg      = data?.average_rating ?? data?.avg_rating ?? 0;
  const total    = data?.total_reviews ?? data?.total ?? 0;
  const reviews  = data?.reviews || data?.ratings || [];
  const dist     = data?.rating_distribution || {};

  const stars = [5, 4, 3, 2, 1];

  return (
    <div>
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">Ratings & Reviews</h1>
          <p className="k-page-sub">{total} total reviews</p>
        </div>
        <button className="k-btn-icon" onClick={load} title="Refresh"><RefreshCw size={16} /></button>
      </div>

      {loading ? (
        <div className="k-loading-center"><div className="k-spinner" /></div>
      ) : (
        <>
          {/* Rating summary */}
          <div className="k-card" style={{ marginBottom: 20 }}>
            <div className="k-grid-2" style={{ alignItems: 'center' }}>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 56, fontWeight: 800, color: 'var(--k-text)', lineHeight: 1 }}>{avg.toFixed(1)}</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 2, margin: '10px 0' }}>
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} size={22} fill={s <= Math.round(avg) ? '#F59E0B' : 'none'} color="#F59E0B" />
                  ))}
                </div>
                <div style={{ fontSize: 14, color: 'var(--k-text-sub)' }}>Based on {total} reviews</div>
              </div>
              <div>
                {stars.map(s => {
                  const count = dist[s] || 0;
                  const pct   = total > 0 ? (count / total) * 100 : 0;
                  return (
                    <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 36, fontSize: 13, fontWeight: 600 }}>
                        {s} <Star size={12} fill="#F59E0B" color="#F59E0B" />
                      </div>
                      <div style={{ flex: 1, height: 8, background: '#F1F5F9', borderRadius: 999 }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: '#F59E0B', borderRadius: 999, transition: 'width .4s' }} />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--k-text-sub)', minWidth: 32 }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Reviews list */}
          <div>
            {reviews.length === 0 ? (
              <div className="k-card">
                <div className="k-empty">
                  <div className="k-empty-icon">⭐</div>
                  <div className="k-empty-title">No reviews yet</div>
                  <div className="k-empty-sub">Customer reviews will appear here as orders are completed</div>
                </div>
              </div>
            ) : (
              reviews.map((r, i) => (
                <div key={i} className="k-card" style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div className="k-avatar-sm">{(r.customer_name || 'C').charAt(0)}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>{r.customer_name || 'Customer'}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {[1, 2, 3, 4, 5].map(s => (
                            <Star key={s} size={14} fill={s <= (r.rating || 0) ? '#F59E0B' : 'none'} color="#F59E0B" />
                          ))}
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{r.rating}</span>
                        </div>
                      </div>
                      {r.review_text || r.comment ? (
                        <p style={{ fontSize: 14, color: 'var(--k-text)', lineHeight: 1.6, margin: 0 }}>{r.review_text || r.comment}</p>
                      ) : null}
                      {r.item_name && (
                        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--k-text-sub)' }}>Order: {r.item_name}</div>
                      )}
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--k-text-muted)' }}>
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
